// The zoom path (breadcrumb) and its focus restore across navigations, for
// AppLayout. Kept apart so the pieces can be tested without the whole shell.
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useZoomStore, routerPathRef } from '@/shared/store/zoomStore';

// Parent-list label for every +New document form in the app. When the
// user lands on /<key>/new, useZoomNavWatcher seeds the zoom path with a single
// "back to <label>" crumb pointing at the list. Add a row whenever a new
// document type ships.
const NEW_FORM_PARENTS: Record<string, string> = {
  '/ar-invoices': 'Invoices',
  '/ap-invoices': 'Bills',
  '/sales-orders': 'Sales orders',
  '/shipments': 'Shipments',
  '/purchase-orders': 'Purchase orders',
  '/receipts': 'Receipts',
  '/contracts': 'Contracts',
  '/manufacturing-orders': 'Manufacturing orders',
  '/journal': 'Journal',
  '/customers': 'Customers',
  '/vendors': 'Vendors',
  '/warehouses': 'Warehouses',
  '/items': 'Items',
  '/item-families': 'Categories',
  '/boms': 'BOMs',
  '/payments': 'Payments',
  '/inventory/transactions': 'Inventory transactions',
  '/sales/price-lists': 'Price lists',
  '/production/routings': 'Routings',
};

  // Watch every navigation and decide what should happen to the zoom path:
  //
  //  1. Intentional zoom traffic (push / popTo) — push() and popTo() set
  //     `intentionalNav: true` right before the caller navigates. We
  //     consume that flag and leave the stack alone — the store action
  //     already updated it correctly.
  //
  //  2. Landing on /<entity>/new — seed a single "back to <list>" crumb
  //     so the user can return to the list with one click. Carries no
  //     prior drill chain; this is a fresh start.
  //
  //  3. Anything else (main-nav click, sub-tab, browser URL bar, button
  //     that calls useNavigate without push) — clear the stack. Without
  //     this, jumping from "Invoices › INV-…" to /customers via main nav
  //     would still show the stale Invoices crumb.
  //
  // currentLabel is intentionally left alone in cases 2 and 3 because
  // child page effects (the destination's useZoomCrumb) run before this
  // parent effect on mount.
export function useZoomNavWatcher(): void {
  const location = useLocation();
  useEffect(() => {
    // Stamp the real router path FIRST: any store subscriber that fires during
    // the clears below (e.g. a still-mounted page's backstop re-seed) must see
    // the NEW location, not the one it was mounted for.
    routerPathRef.current = location.pathname;
    const zs = useZoomStore.getState();
    // Home ('/') never owns a zoom crumb. Drilling into a record from the Home
    // canvas seeds frames (useHomeNav "Home" push + the destination's async
    // backstop), and a lazy detail page can seed its list crumb in a post-nav
    // commit; returning to Home must land clean regardless of how we got here.
    // A leftover frame here isn't just a stray "Quotes ›" crumb — it also
    // hijacks the browser-back zoom handler — so clear it outright. pendingFocus
    // is preserved so a zoom-back-to-Home still restores the prior focus.
    if (location.pathname === '/') {
      useZoomStore.setState({ stack: [], currentLabel: '', navFor: null, intentionalNav: false });
      return;
    }
    if (zs.intentionalNav) {
      useZoomStore.setState({ intentionalNav: false });
      return;
    }
    // A lazy detail page's useZoomBackstop seeds its crumb in a SEPARATE commit
    // (the Suspense boundary resolves after the pathname already changed), so
    // this watcher never runs to consume a boolean flag — it would leak onto
    // the next page. Instead the backstop stamps `navFor` with its OWN path; we
    // honour the seed only while we're actually on that page. A leftover stamp
    // from a prior page therefore can't preserve the stack when the user jumps
    // elsewhere (the bug: an invoice's "Invoices ›" crumb surviving onto an
    // Admin Center page).
    if (zs.navFor && zs.navFor === location.pathname) {
      useZoomStore.setState({ navFor: null });
      return;
    }
    if (location.pathname.endsWith('/new')) {
      const parent = location.pathname.replace(/\/new$/, '');
      const label = NEW_FORM_PARENTS[parent];
      useZoomStore.setState({
        stack: label ? [{ path: parent, label }] : [],
        navFor: null,
      });
      return;
    }
    useZoomStore.setState({ stack: [], navFor: null });
  }, [location.pathname]);
}

export function useZoomFocusRestore(): void {
  // Zoom-OUT focus restore: when popTo() set a pendingFocus selector, re-focus
  // (and highlight) that element once the destination page has rendered. The
  // list it returns to loads async, so retry over a couple of seconds until the
  // element appears, then clear the pending selector.
  const pendingFocus = useZoomStore((s) => s.pendingFocus);
  useEffect(() => {
    if (!pendingFocus) return;
    let raf = 0;
    let tries = 0;
    const tryFocus = () => {
      // Only on the page it belongs to (D1: a zoom-out by Back navigates a
      // moment later; the record still on screen may have a grid of its own).
      const forPath = useZoomStore.getState().pendingFocusFor;
      const onTarget = !forPath || routerPathRef.current === forPath.split(/[?#]/)[0];
      const el = onTarget ? document.querySelector<HTMLElement>(pendingFocus) : null;
      if (el) {
        el.focus();
        el.scrollIntoView({ block: 'nearest' });
        useZoomStore.getState().setPendingFocus(null);
        return;
      }
      if (tries++ < 120) raf = requestAnimationFrame(tryFocus);
      else useZoomStore.getState().setPendingFocus(null);
    };
    raf = requestAnimationFrame(tryFocus);
    return () => cancelAnimationFrame(raf);
  }, [pendingFocus]);
}
